import React from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { CustomLegend } from "modules/LocationReports/components";
import "modules/LocationReports/components/SimpleBarChart/SimpleBarChart.scss";
import { getTranslationOrValue } from "modules/LocationReports/utils";
import { REPORTS_COLORS, defaultFormatter, extractAllNameFields, isNumOrStr } from "utils/common";

import type { ReportColor } from "models";
import type { ReportThreshold } from "modules/LocationReports";
import type { TooltipProps } from "recharts";

type SimpleBarChartProps = {
  data: Record<string, string | number>[];
  unit?: string;
  colors?: ReportColor[];
  thresholds?: ReportThreshold[];
};

type BarLabelProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  value: number;
  index: number;
  dataKey: string;
};

const VerticalOffset = -6;

const formatDuration = (seconds: number): string => {
  const milliseconds = seconds * 1000;

  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  const secs = Math.floor((milliseconds % (1000 * 60)) / 1000);

  return [hours.toString().padStart(2, "0"), minutes.toString().padStart(2, "0"), secs.toString().padStart(2, "0")].join(":");
};

export const SimpleBarChart = ({ data, colors, unit, thresholds }: SimpleBarChartProps) => {
  const { t } = useTranslation();

  let iBar = 0;

  const renderCustomTooltip = ({ active, payload, label, formatter, separator = ":" }: TooltipProps<number, string>) => {
    if (!active) return null;

    return (
      <div
        className="recharts-default-tooltip"
        style={{ margin: 0, padding: 10, backgroundColor: "white", border: "1px solid rgb(204, 204, 204)", whiteSpace: "nowrap" }}
      >
        <p className="recharts-tooltip-label" style={{ margin: 0 }}>
          {label}
        </p>
        <ul className="recharts-tooltip-item-list" style={{ padding: 0, margin: 0 }}>
          {payload?.map((entry: any, i: number) => {
            if (entry.type === "none") return null;

            const itemStyle = {
              display: "block",
              paddingTop: 4,
              paddingBottom: 4,
              color: entry.color || "#000"
            };

            const formatData = entry.formatter || formatter || defaultFormatter;
            let { name, value, unit } = entry;
            if (formatData) {
              const formatted = formatData(value, name, entry, i, payload);
              if (Array.isArray(formatted)) [value, name] = formatted;
              else value = formatted;
            }

            let totalTooltip = null;
            let percentTooltip = null;

            if (entry.payload) {
              let updatedkey: string | null = null;
              let key = Object.keys(entry.payload).find(k => k.startsWith("total." + name + "."));
              if (key) updatedkey = key.replace("total." + name + ".", "");
              else if (i === payload.length - 1) {
                key = Object.keys(entry.payload).find(k => k.startsWith("total._last_."));
                if (key) updatedkey = key.replace("total._last_.", "");
              }

              if (updatedkey && key) {
                updatedkey = t(`v8_${updatedkey}`) || updatedkey;
                totalTooltip = updatedkey + ": " + entry.payload[key];
              }

              updatedkey = null;
              key = Object.keys(entry.payload).find(k => k === "percent." + name);
              if (key) updatedkey = key.replace("percent.", "");

              if (updatedkey && key) {
                updatedkey = t(`v8_${updatedkey}`) || updatedkey;
                percentTooltip = updatedkey + ": " + entry.payload[key] + " %";
              }
            }

            if (unit === " min") {
              value = formatDuration(value);
              unit = "";
            }

            return (
              <React.Fragment key={`tooltip-item-${i}`}>
                <li className="recharts-tooltip-item" style={itemStyle}>
                  {isNumOrStr(name) ? (
                    <>
                      <span className="recharts-tooltip-item-name">{getTranslationOrValue(t, String(name))}</span>
                      <span className="recharts-tooltip-item-separator">{separator}</span>
                    </>
                  ) : null}
                  <span className="recharts-tooltip-item-value">{`${value} `}</span>
                  {unit && <span className="recharts-tooltip-item-unit">{getTranslationOrValue(t, unit)}</span>}
                </li>
                {totalTooltip && (
                  <li className="recharts-tooltip-item" style={itemStyle}>
                    <span className="recharts-tooltip-item-value">{totalTooltip}</span>
                  </li>
                )}
                {percentTooltip && (
                  <li className="recharts-tooltip-item" style={itemStyle}>
                    <span className="recharts-tooltip-item-value">{percentTooltip}</span>
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderCustomBarLabel = ({ x, y, width, index }: BarLabelProps) => {
    let label = null;
    const dataKey = data[index]?.dataKey;
    if (data[index]) {
      let key = Object.keys(data[index]).find(k => k.startsWith("total." + dataKey + "."));
      if (!key) key = Object.keys(data[index]).find(k => k.startsWith("total._last_."));

      if (key) label = data[index][key];
    }

    if (!label) return <span></span>;

    return (
      <text x={x + width / 2} y={y} fill="#000000" textAnchor="middle" dy={VerticalOffset}>
        {label || ""}
      </text>
    );
  };

  const renderCustomThresholdsLegend = () => {
    if (!thresholds?.length) return null;

    return (
      <ul className="recharts-default-legend" style={{ padding: 0, margin: 0, textAlign: "center" }}>
        {thresholds.map((th: ReportThreshold, index: number) => (
          <li key={index} className="recharts-legend-item" style={{ display: "inline-block", marginRight: "10px" }}>
            <svg
              className="recharts-surface"
              width="14"
              height="14"
              viewBox="0 0 32 32"
              version="1.1"
              style={{ display: "inline-block", verticalAlign: "middle", marginRight: "4px" }}
            >
              <line strokeWidth="4" fill="none" stroke={th.color} x1="0" y1="16" x2="32" y2="16" className="recharts-legend-icon"></line>
            </svg>
            <span className="recharts-legend-item-text">{t(`v8_${th.name}`) || th.name}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="SimpleBarChart">
      <ResponsiveContainer width="100%" height={450} className="margin-auto">
        <BarChart data={data} margin={{ top: 20, right: 30, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          {unit === " min" && (
            <YAxis
              width={80}
              tickFormatter={value => {
                const hours = Math.floor(value / 3600);
                const minutes = Math.floor((value % 3600) / 60);
                const seconds = value % 60;

                const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

                return formattedTime;
              }}
            />
          )}
          {unit !== " min" && <YAxis unit={unit === "%" ? "%" : undefined} domain={unit === "%" ? [0, 100] : ["auto", "auto"]} />}
          <Tooltip content={renderCustomTooltip} />
          <Legend content={<CustomLegend />} />

          {extractAllNameFields(data)
            .filter((k: string) => k !== "name" && !k.startsWith("total.") && !k.startsWith("percent."))
            .map((k: string, i: number) => {
              const item = colors?.find(col => col.name === k);
              const color = item?.color || null;

              return (
                <Bar
                  key={"bar" + i}
                  maxBarSize={100}
                  dataKey={k}
                  fill={color || REPORTS_COLORS[iBar++ % REPORTS_COLORS.length]}
                  unit={unit && (t(unit) || unit)}
                  label={params => renderCustomBarLabel({ dataKey: k, ...params })}
                  isAnimationActive={false}
                />
              );
            })}

          {thresholds && thresholds.map((t, i) => <ReferenceLine key={"threshold" + i} y={t.y} isFront={true} stroke={t.color} />)}
        </BarChart>
      </ResponsiveContainer>
      <ResponsiveContainer width="100%" height={30} className="margin-auto">
        <BarChart margin={{ top: 10, right: 30, bottom: 5 }}>
          <Legend content={renderCustomThresholdsLegend} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
